/**
 * SamplePackCta — "Try it with sample data" button.
 *
 * GH-268 Phase 4. Primary entry point for first-time users to seed 3 demo
 * onboarding packages and immediately start the coach-mark tour.
 *
 * Behavior:
 *   1. Click → POST /api/onboarding-packages/sample-pack (via useCreateSamplePack)
 *   2. On success → useTour().start() to launch the 8-step tour
 *
 * Used on:
 *   - Welcome empty-state on the home dashboard (secondary CTA next to
 *     "Create your first package")
 *   - Anywhere the workspace appears empty / template-less
 *
 * The button surfaces a yellow accent + sparkle icon so it reads as a
 * playful, low-stakes path. The tooltip reassures the user that sample
 * data is reversible.
 */

import { useTranslation } from 'react-i18next'
import { Sparkles } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useTour } from '../onboarding-tour/use-tour'
import { useCreateSamplePack } from './use-onboarding-packages'

interface SamplePackCtaProps {
  /** Optional className override for sizing/placement in different layouts. */
  className?: string
  /** Visual variant — "secondary" sits next to a primary brand CTA. */
  variant?: 'primary' | 'secondary'
}

export function SamplePackCta({ className, variant = 'secondary' }: SamplePackCtaProps) {
  const { t } = useTranslation('packages')
  const createSamplePack = useCreateSamplePack()
  const { start } = useTour()

  const handleClick = async () => {
    try {
      await createSamplePack.mutateAsync()
      // Kick off coach-marks once seed succeeds.
      start()
    } catch (err) {
      // Surface to console; component-level error toast is intentionally
      // omitted here to keep the empty-state minimal. Users can retry.
      console.error('SamplePackCta — failed to seed sample pack:', err)
    }
  }

  const isPending = createSamplePack.isPending

  const baseClasses =
    'inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60'

  const variantClasses =
    variant === 'primary'
      ? // Yellow primary — used when alone (no other CTA next to it)
        'bg-yellow-400 text-yellow-950 hover:bg-yellow-300 ring-1 ring-yellow-500/40'
      : // Yellow-tinted secondary — sits next to a brand-colored primary
        'bg-yellow-50 text-yellow-900 hover:bg-yellow-100 ring-1 ring-yellow-300/60 dark:bg-yellow-900/20 dark:text-yellow-100 dark:hover:bg-yellow-900/30 dark:ring-yellow-400/30'

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isPending}
      title={t('samplePackCta.tooltip')}
      aria-label={t('samplePackCta.ariaLabel')}
      data-testid="sample-pack-cta"
      className={cn(baseClasses, variantClasses, className)}
    >
      <Sparkles className="h-4 w-4" aria-hidden="true" />
      {isPending ? t('samplePackCta.loading') : t('samplePackCta.label')}
    </button>
  )
}
